import { useState } from 'react';
import type { FormEvent } from 'react';
import type { Accomplishment, DecorationKind, SubmitClaimInput } from '@clan-heart/shared';
import { DECORATION_CATEGORIES } from '@clan-heart/shared';
import { claimStateFor, canClaimAgain } from '../lib/claimState.js';
import { StatusBadge } from './StatusBadge.js';
import { Button } from './Button.js';
import styles from './AccomplishmentCard.module.css';

interface AccomplishmentCardProps {
  accomplishment: Accomplishment;
  onClaim: (input: SubmitClaimInput) => Promise<unknown>;
  onWithdraw: (claimId: string) => Promise<unknown>;
}

const KIND_LABELS: Record<DecorationKind, string> = { ribbon: 'Ribbon', medal: 'Medal', ensign: 'Ensign' };

const STAR_LABELS = { none: null, bronze: 'Bronze star', silver: 'Silver star', gold: 'Gold star' } as const;

export function AccomplishmentCard({ accomplishment, onClaim, onWithdraw }: AccomplishmentCardProps) {
  const { myClaim, repeatable, myApprovedCount, myStars } = accomplishment;
  const state = claimStateFor(myClaim);
  const starLabel = STAR_LABELS[myStars.tier];
  const [formOpen, setFormOpen] = useState(false);
  const [note, setNote] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onClaim({
        note: note.trim() === '' ? undefined : note.trim(),
        evidenceUrl: evidenceUrl.trim() === '' ? undefined : evidenceUrl.trim(),
      });
      setFormOpen(false);
      setNote('');
      setEvidenceUrl('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleWithdraw() {
    setWithdrawing(true);
    setError(null);
    try {
      if (myClaim) await onWithdraw(myClaim.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setWithdrawing(false);
    }
  }

  return (
    <article className={styles.card}>
      <div className={styles.top}>
        <div className={`${styles.art} ${styles[`art_${accomplishment.decoration}`]}`}>
          {accomplishment.art ? (
            <img src={accomplishment.art} alt="" />
          ) : (
            <span className={styles.artMissing}>{KIND_LABELS[accomplishment.decoration]}</span>
          )}
        </div>
        <div className={styles.body}>
          <p className={styles.kind}>
            {KIND_LABELS[accomplishment.decoration]} ·{' '}
            {DECORATION_CATEGORIES.find((c) => c.value === accomplishment.category)?.label}
          </p>
          <div className={styles.titleRow}>
            <h3 className={styles.title}>{accomplishment.title}</h3>
            <span className={styles.points}>{accomplishment.points} pts</span>
          </div>
          {accomplishment.description && (
            <p className={styles.description}>{accomplishment.description}</p>
          )}
          {repeatable && (
            <p className={styles.repeatableMeta}>
              Earned {myApprovedCount}×{starLabel ? ` · ${starLabel}` : ''}
              {myStars.next !== null ? ` · next at ${myStars.next}` : ''}
            </p>
          )}
        </div>
        <div className={styles.actions}>
          <StatusBadge state={state} />
          {state === 'pending' && (
            <Button type="button" variant="danger" onClick={handleWithdraw} disabled={withdrawing}>
              Withdraw
            </Button>
          )}
          {canClaimAgain(accomplishment) && !formOpen && (
            <Button type="button" variant="primary" onClick={() => setFormOpen(true)}>
              Claim
            </Button>
          )}
        </div>
      </div>

      {state === 'rejected' && myClaim?.reviewNote && (
        <p className={styles.reviewNote}>Reviewer note: {myClaim.reviewNote}</p>
      )}

      {formOpen && (
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label htmlFor={`note-${accomplishment.id}`}>Note (optional)</label>
            <textarea
              id={`note-${accomplishment.id}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
          </div>
          <div className={styles.field}>
            <label htmlFor={`evidence-${accomplishment.id}`}>Evidence link (optional)</label>
            <input
              id={`evidence-${accomplishment.id}`}
              type="url"
              placeholder="https://..."
              value={evidenceUrl}
              onChange={(e) => setEvidenceUrl(e.target.value)}
            />
          </div>
          {error && <p className={styles.error}>{error}</p>}
          <div className={styles.formActions}>
            <Button type="submit" variant="primary" disabled={submitting}>
              Submit claim
            </Button>
            <Button type="button" variant="ghost" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {!formOpen && error && <p className={styles.error}>{error}</p>}
    </article>
  );
}
